import { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, UploadSimple, FileCsv, CheckCircle, Warning, MicrosoftExcelLogo } from "@phosphor-icons/react";
import * as XLSX from "xlsx";

// Simple CSV parser that handles quoted fields and embedded commas.
function parseCSV(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") { cur.push(field); field = ""; }
      else if (ch === "\n") { cur.push(field); rows.push(cur); cur = []; field = ""; }
      else if (ch === "\r") { /* skip */ }
      else field += ch;
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.some((c) => c && c.trim() !== ""));
}

// Parse an xlsx/xls Blob → array-of-arrays (same shape as parseCSV).
async function parseXlsx(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const firstSheet = wb.SheetNames[0];
  if (!firstSheet) return [];
  const sheet = wb.Sheets[firstSheet];
  // header:1 returns array-of-arrays; defval ensures missing cells → ""
  const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false });
  // Coerce every cell to a trimmed string
  return aoa
    .map((r) => r.map((v) => (v === null || v === undefined ? "" : String(v).trim())))
    .filter((r) => r.some((c) => c !== ""));
}

const HEADER_ALIASES = {
  name: ["name", "full name", "customer", "customer name", "client", "client name", "fullname"],
  phone: ["phone", "telephone", "mobile", "cell", "phone number", "contact", "phone_number"],
  email: ["email", "e-mail", "email address", "e_mail"],
  notes: ["notes", "note", "comments", "remarks", "description"],
};

function autoMap(headers) {
  const map = { name: -1, phone: -1, email: -1, notes: -1 };
  headers.forEach((h, i) => {
    const n = (h || "").toLowerCase().trim();
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (map[field] === -1 && aliases.includes(n)) map[field] = i;
    }
  });
  // Handle first_name / last_name → combined name
  if (map.name === -1) {
    const fi = headers.findIndex((h) => ["first name", "firstname", "first"].includes((h || "").toLowerCase().trim()));
    if (fi !== -1) map.name = fi;
  }
  return map;
}

export default function ImportCustomersModal({ open, onClose, onImported }) {
  const [text, setText] = useState("");
  const [xlsxRows, setXlsxRows] = useState(null); // array-of-arrays when an xlsx was uploaded
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dedupe, setDedupe] = useState("phone_or_email");

  const parsed = useMemo(() => {
    // Prefer xlsx rows if present
    const rows = xlsxRows || (text.trim() ? parseCSV(text) : null);
    if (!rows || rows.length === 0) return null;
    const headers = rows[0].map((h) => (h ?? "").toString().trim());
    const dataRows = rows.slice(1);
    return { headers, dataRows, map: autoMap(headers) };
  }, [text, xlsxRows]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setResult(null);
    setFileName(f.name);
    const lower = f.name.toLowerCase();
    try {
      if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) {
        const rows = await parseXlsx(f);
        if (!rows.length) { toast.error("The spreadsheet looks empty."); return; }
        setXlsxRows(rows);
        setText(""); // clear any pasted CSV so xlsx takes precedence
        toast.success(`Parsed ${rows.length - 1} rows from ${f.name}`);
      } else {
        // treat as CSV
        setXlsxRows(null);
        setText(await f.text());
      }
    } catch (err) {
      toast.error(`Could not parse the file: ${err.message || err}`);
    } finally {
      if (e.target) e.target.value = ""; // allow re-select of same file
    }
  };

  const submit = async () => {
    if (!parsed || parsed.dataRows.length === 0) return toast.error("Nothing to import");
    const { map, dataRows, headers } = parsed;
    // Build: first check last_name if first_name was detected
    const firstIdx = headers.findIndex((h) => ["first name", "firstname", "first"].includes((h || "").toLowerCase().trim()));
    const lastIdx = headers.findIndex((h) => ["last name", "lastname", "last", "surname"].includes((h || "").toLowerCase().trim()));

    const customers = dataRows.map((r) => {
      let name = (r[map.name] || "").trim();
      if (firstIdx !== -1) {
        const first = (r[firstIdx] || "").trim();
        const last = lastIdx !== -1 ? (r[lastIdx] || "").trim() : "";
        const combined = [first, last].filter(Boolean).join(" ").trim();
        if (combined) name = combined;
      }
      return {
        name,
        phone: map.phone !== -1 ? (r[map.phone] || "").trim() : "",
        email: map.email !== -1 ? (r[map.email] || "").trim() : "",
        notes: map.notes !== -1 ? (r[map.notes] || "").trim() : "",
      };
    });

    setSubmitting(true);
    try {
      const { data } = await api.post("/customers/import", { customers, dedupe_on: dedupe });
      setResult(data);
      toast.success(`${data.imported} imported · ${data.skipped_duplicate} skipped`);
      onImported?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setText(""); setXlsxRows(null); setFileName(""); setResult(null); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[92vh] border border-[#E5E7EB] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="overline mb-1">IMPORT</div>
            <h2 className="font-heading font-black text-2xl tracking-tight">Import customers</h2>
          <div className="text-[12px] text-[#4B5563] mt-1">Upload a CSV or Excel file from your old POS, or paste CSV text.</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#FFFFFF]" data-testid="import-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-4">
          {/* Upload / paste */}
          {!result && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="border-2 border-dashed border-[#E5E7EB] hover:border-[#121216] p-6 text-center cursor-pointer transition-colors rounded-xl" data-testid="import-file-label">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <FileCsv size={28} weight="duotone" className="text-[#1E5FEB]" />
                    <MicrosoftExcelLogo size={28} weight="duotone" className="text-[#2F8F6E]" />
                  </div>
                  <div className="text-sm font-semibold">Upload CSV or Excel</div>
                  <div className="text-xs text-zinc-500 mt-1">{fileName || ".csv  ·  .xlsx  ·  .xls"}</div>
                  <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" className="hidden" onChange={onFile} data-testid="import-file-input" />
                </label>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Or paste CSV</div>
                  <textarea
                    value={text}
                    onChange={(e) => { setText(e.target.value); setXlsxRows(null); setFileName(""); setResult(null); }}
                    rows={6}
                    placeholder={`name,phone,email,notes\nJane Doe,555-0102,jane@example.com,VIP`}
                    className="w-full p-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-xs font-mono rounded-lg bg-white"
                    data-testid="import-textarea"
                  />
                </div>
              </div>

              {/* Preview + mapping */}
              {parsed && parsed.headers.length > 0 && (
                <div className="border border-[#E5E7EB]">
                  <div className="px-4 py-2 border-b border-[#E5E7EB] bg-[#F8FAFC] flex items-center justify-between">
                    <div className="overline">COLUMN MAPPING</div>
                    <div className="text-xs font-mono text-zinc-500">{parsed.dataRows.length} rows detected</div>
                  </div>
                  <div className="p-4 grid grid-cols-2 gap-3">
                    {["name", "phone", "email", "notes"].map((f) => (
                      <MapField key={f} field={f} headers={parsed.headers} value={parsed.map[f]}
                        onChange={(v) => setText((t) => t) /* no-op; autoMap only */}
                      />
                    ))}
                  </div>
                  <div className="border-t border-[#E5E7EB]">
                    <div className="overflow-auto max-h-48">
                      <table className="w-full text-xs">
                        <thead className="bg-[#F8FAFC] sticky top-0">
                          <tr className="text-left uppercase tracking-wider text-zinc-500">
                            <th className="p-2">Name</th><th className="p-2">Phone</th><th className="p-2">Email</th><th className="p-2">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.dataRows.slice(0, 8).map((r, idx) => (
                            <tr key={idx} className="border-t border-[#E5E7EB]">
                              <td className="p-2 font-medium">{parsed.map.name !== -1 ? r[parsed.map.name] : "—"}</td>
                              <td className="p-2 font-mono">{parsed.map.phone !== -1 ? r[parsed.map.phone] : "—"}</td>
                              <td className="p-2">{parsed.map.email !== -1 ? r[parsed.map.email] : "—"}</td>
                              <td className="p-2 truncate max-w-[180px]">{parsed.map.notes !== -1 ? r[parsed.map.notes] : "—"}</td>
                            </tr>
                          ))}
                          {parsed.dataRows.length > 8 && (
                            <tr><td colSpan={4} className="p-2 text-center text-zinc-400">+ {parsed.dataRows.length - 8} more rows</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Dedupe */}
              <div>
                <div className="overline mb-2">Skip duplicates that match</div>
                <div className="flex gap-2 flex-wrap" data-testid="dedupe-options">
                  {[
                    { id: "phone_or_email", label: "Phone OR Email" },
                    { id: "phone", label: "Phone only" },
                    { id: "email", label: "Email only" },
                    { id: "none", label: "Don't dedupe" },
                  ].map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setDedupe(o.id)}
                      className={`h-9 px-3 text-xs font-semibold border transition-colors ${dedupe === o.id ? "bg-[#121216] text-white border-[#121216]" : "bg-white border-[#E5E7EB]"}`}
                      data-testid={`dedupe-${o.id}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Result */}
          {result && (
            <div className="bg-white border border-[#E5E7EB] p-6" data-testid="import-result">
              <CheckCircle size={40} weight="duotone" className="text-[#2F8F6E] mb-3" />
              <div className="font-heading font-bold text-2xl tracking-tight mb-4">Import complete</div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <ResultStat label="Imported" value={result.imported} color="#2F8F6E" />
                <ResultStat label="Skipped (dup)" value={result.skipped_duplicate} color="#C98B24" />
                <ResultStat label="Invalid" value={result.skipped_invalid} color="#DC2626" />
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-4 border border-[#E5E7EB] p-3 text-xs">
                  <div className="flex items-center gap-2 text-[#DC2626] font-semibold mb-2"><Warning size={14} weight="bold" /> Errors</div>
                  <ul className="space-y-1 font-mono text-zinc-600">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#E5E7EB] bg-[#FFFFFF]">
          {result ? (
            <>
              <button onClick={reset} className="h-11 px-4 border border-[#E5E7EB] text-[13px] font-semibold bg-white hover:border-[#121216]">IMPORT MORE</button>
              <button onClick={onClose} data-testid="import-done" className="h-11 px-5 bg-[#121216] hover:bg-[#1E5FEB] text-white text-[13px] font-semibold">DONE</button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="h-11 px-4 border border-[#E5E7EB] text-[13px] font-semibold bg-white hover:border-[#121216]">CANCEL</button>
              <button
                onClick={submit}
                disabled={submitting || !parsed || parsed.dataRows.length === 0 || parsed.map.name === -1}
                data-testid="import-submit"
                className="h-11 px-5 bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-zinc-300 text-white text-[13px] font-semibold flex items-center gap-2"
              >
                <UploadSimple size={14} weight="bold" /> {submitting ? "IMPORTING…" : `IMPORT ${parsed?.dataRows.length || 0} ROWS`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MapField({ field, headers, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">{field}</div>
      <div className={`h-9 px-3 border flex items-center text-xs ${value === -1 ? "border-[#E5E7EB] text-zinc-400" : "border-[#121216] bg-[#121216] text-white font-mono"}`}>
        {value === -1 ? "— not mapped —" : headers[value]}
      </div>
    </div>
  );
}

function ResultStat({ label, value, color }) {
  return (
    <div className="border border-[#E5E7EB] p-3">
      <div className="overline mb-1">{label}</div>
      <div className="font-mono font-bold text-3xl" style={{ color }}>{value}</div>
    </div>
  );
}
